import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, Observable} from 'rxjs';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo} from '@shared/store/selectors/security.selector';

/**
 * THE COMPOSER DRAFTS (ITP-E-194-03): what a person typed in a conversation and has not sent, kept in this browser per
 * conversation, so the thread puts it back when the conversation opens again and the list marks the row "Draft: …".
 * Drafts are the composer's HTML, under one localStorage key per user. Sending, or emptying the composer, clears one.
 */
@Injectable({providedIn: 'root'})
export class MessengerDrafts {
  private static readonly keyPrefix = 'itp-chat-drafts:';
  private readonly drafts = new BehaviorSubject<Record<string, string>>({});
  private storageKey: string = null;

  /** Every draft of the signed-in user, keyed by conversation id. */
  readonly drafts$: Observable<Record<string, string>> = this.drafts.asObservable();

  constructor(securityStore: Store<SecurityState>) {
    securityStore.pipe(select(getUserInfo)).subscribe(user => {
      const key = user?.email ? `${MessengerDrafts.keyPrefix}${user.email.toLowerCase()}` : null;
      if (key !== this.storageKey) {
        this.storageKey = key;
        this.drafts.next(this.load());
      }
    });
  }

  /** The draft's HTML, or '' when the conversation has none. */
  get(conversationId: string): string {
    return (conversationId && this.drafts.value[conversationId]) || '';
  }

  /** Keeps the composer's HTML for the conversation; an empty composer clears its draft. */
  save(conversationId: string, html: string): void {
    if (!conversationId) {
      return;
    }
    if (!MessengerDrafts.textOf(html)) {
      this.clear(conversationId);
      return;
    }
    if (this.drafts.value[conversationId] !== html) {
      this.write({...this.drafts.value, [conversationId]: html});
    }
  }

  clear(conversationId: string): void {
    if (conversationId && conversationId in this.drafts.value) {
      const {[conversationId]: _, ...rest} = this.drafts.value;
      this.write(rest);
    }
  }

  /** A draft as the list shows it: the composer's HTML as one line of plain text. */
  static textOf(html: string): string {
    if (!html) {
      return '';
    }
    const element = document.createElement('div');
    element.innerHTML = html;
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private load(): Record<string, string> {
    if (!this.storageKey) {
      return {};
    }
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      return stored && typeof stored === 'object' ? stored : {};
    } catch {
      return {};
    }
  }

  private write(drafts: Record<string, string>): void {
    this.drafts.next(drafts);
    if (!this.storageKey) {
      return;
    }
    try {
      if (Object.keys(drafts).length) {
        localStorage.setItem(this.storageKey, JSON.stringify(drafts));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch {
      // Storage full or blocked: the draft lives for this tab only.
    }
  }
}
